import React, {memo, useState, useCallback, useEffect, useMemo} from 'react';
import {AdBannerData} from '@/app/interfaces/ad';
import {debounce} from '@/common/helpers/utils/debounce';
import {AdpartnerAdBanner} from '../AdpartnerAdBanner';
import {AdsenseAdBanner} from '../AdsenseAdBanner';
import {DfpAdBanner} from '../DfpAdBanner';
import {MarketGidAdBanner} from '../MarketGidAdBanner';

export interface AdBannerEntryProps {
	className?: string;
	data: AdBannerData;
	interfaceName: string;
	reserveSize?: boolean | 'width' | 'height';
	placeholder?: boolean;
}

export const AdBannerEntry = memo<AdBannerEntryProps>(props => {
	const {className, data, interfaceName, reserveSize, placeholder} = props;

	const viewportMediaQuery = useMemo(() => {
		return null; // Temporary disabled

		if (data.minViewportWidth || data.minViewportWidth) {
			return null;
		}

		const queries = [
			data.minViewportWidth && `(min-width: ${data.minViewportWidth}px)`,
			data.maxViewportWidth && `(max-width: ${data.maxViewportWidth}px)`,
		];

		return window.matchMedia(queries.filter(Boolean).join(' and '));
	}, [data]);

	const checkIsShown = useCallback(() => {
		if (viewportMediaQuery && !viewportMediaQuery.matches) {
			return false;
		}

		if ((data.minScreenWidth || data.maxScreenWidth) && typeof window === 'undefined') {
			return false;
		}

		if (data.minScreenWidth && window.innerWidth < data.minScreenWidth) {
			return false;
		}

		if (data.maxScreenWidth && window.innerWidth > data.maxScreenWidth) {
			return false;
		}

		return true;
	}, [data, viewportMediaQuery]);

	const [shown, setShown] = useState(checkIsShown);

	// Track the viewport size media query match change
	useEffect(() => {
		if (!viewportMediaQuery) {
			return undefined;
		}

		const handleChange = () => {
			setShown(checkIsShown());
		};
		viewportMediaQuery.addListener(handleChange);
		return () => {
			viewportMediaQuery.removeListener(handleChange);
		};
	}, [viewportMediaQuery]);

	// Re-check screen-width gated banners when the browser viewport changes.
	useEffect(() => {
		if (!data.minScreenWidth && !data.maxScreenWidth) {
			return undefined;
		}

		const handleResize = debounce(() => {
			setShown(checkIsShown());
		}, 500);

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			handleResize.cancel();
		};
	}, [checkIsShown, data.maxScreenWidth, data.minScreenWidth]);

	if (!shown) {
		return null;
	}

	switch (data.provider) {
		case 'dfp':
			return (
				<DfpAdBanner
					className={className}
					unitPath={data.unitPath}
					divId={data.blockId}
					sizes={data.sizes}
					sizeMapping={data.sizeMapping}
					collapseEmpty={data.collapseEmpty}
					reserveSize={reserveSize}
					lazyLoading={data.lazyLoading}
					isBranding={data.isBranding}
					forceSafeFrame={data.forceSafeFrame}
					analyticsLabel={data.analyticsLabel}
					interfaceName={interfaceName}
					placeholder={placeholder}
					extraScripts={data.extraScripts}
					forceMinSize={data.forceMinSize}
				/>
			);

		case 'adsense':
			return (
				<AdsenseAdBanner
					className={className}
					client={data.client}
					slot={data.slot}
					width={data.width}
					height={data.height}
					extraAttributes={data.extraAttributes}
					collapseEmpty={data.collapseEmpty}
					analyticsLabel={data.analyticsLabel}
					interfaceName={interfaceName}
					placeholder={placeholder}
				/>
			);

		case 'adpartner':
			return (
				<AdpartnerAdBanner
					className={className}
					unitId={data.unitId}
					width={data.width}
					height={data.height}
					analyticsLabel={data.analyticsLabel}
					interfaceName={interfaceName}
					placeholder={placeholder}
				/>
			);

		case 'marketGid':
			return (
				<MarketGidAdBanner
					className={className}
					blockId={data.blockId}
					scriptSrc={data.scriptSrc}
					placeholder={placeholder}
				/>
			);
	}
});

AdBannerEntry.displayName = 'AdBannerEntry';
